import { useFormikContext } from 'formik'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DepositBankDetails } from '@pages/Traders-Room/Deposit/components/DepositBankDetails'

import { useModal } from '@context/ModalsContext'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'

import { useGAEvents } from '@utils/callGAEvents'
import { useWindowResize } from '@utils/domUtils'

import { MasterTransaction } from '@model/MasterTransaction'
import { isPaymentProviderBankType } from '@model/PaymentProviderType'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingExit } from '@hooks/useFundingExit'
import { usePersistentWindow } from '@hooks/usePersistentWindow'

import { Box } from '@shared/components/Box'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { FundingTemplate } from '@shared/templates/FundingTemplate'

import type { DepositFormValues } from '../DepositFlowLayout/types'
import { BankReferenceModal } from '../modals/BankReference.modal'

export const DepositPendingContent: React.FC<{
  transaction?: MasterTransaction
  providerCategoryId?: number
  onViewHistory: () => void
  onNewDeposit: () => void
}> = ({ transaction, providerCategoryId, onViewHistory, onNewDeposit }) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const { values } = useFormikContext<DepositFormValues>()
  const { formatMoney } = useFormatNumber()
  const [isReferenceCopied, setIsReferenceCopied] = useState(false)
  const { handleExit } = useFundingExit()
  const contentRef = useRef<HTMLDivElement>(null)
  const { callGAEvents } = useGAEvents()

  const providerTx = transaction?.walletTransactions?.[0]?.paymentProviderTransaction

  const dataKeyValue = providerTx?.response?.redirectData?.dataKeyValue
  const isPixCase = Boolean(dataKeyValue?.length)

  const isBank = isPaymentProviderBankType(providerCategoryId, transaction) && !isPixCase
  const provider = values?.paymentProvider

  const amountValue = Number(values?.amount || 0)
  const summaryCurrency =
    provider?.currency?.id ||
    values?.wallet?.currency?.id ||
    values?.tradingAccount?.currency?.id ||
    'USD'

  const bankReferenceModal = useModal(BankReferenceModal, {
    reference: transaction?.referenceId ?? '',
    onCopy: () => setIsReferenceCopied(true),
    initialCanClose: false,
  })

  const handleActionWithReferenceCheck = useCallback(
    (action: () => void) => {
      if (isBank && !isReferenceCopied) {
        bankReferenceModal.open({
          onClose: action,
        })
      } else {
        action()
      }
    },
    [isBank, isReferenceCopied, bankReferenceModal]
  )

  usePersistentWindow(useCallback(() => isBank && !isReferenceCopied, [isBank, isReferenceCopied]))

  useEffect(() => {
    callGAEvents({
      deposit_amount: amountValue,
      deposit_currency: summaryCurrency,
      deposit_destination: values?.wallet ? 'wallet' : 'trading_account',
      deposit_provider: provider?.name || '',
      deposit_provider_type: isBank ? 'bank' : 'other',
    }).Deposit.Transaction.pending()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FundingTemplate
      contentRef={contentRef}
      onExit={() => handleActionWithReferenceCheck(handleExit)}
      onBack={() => handleActionWithReferenceCheck(handleExit)}
    >
      <Box flex direction='column' gap='x32' align='center' minHeight='100%'>
        {!isBank && !isPixCase && (
          <ContentBlock
            variant='voucher'
            icon={
              <FeatureIcon
                size='lg'
                icon={ArrowCircleIcon.UpRight}
                bg='surface-info-p-high-em'
                color='text-white'
              />
            }
            title={t('Your transaction is in progress')}
            description={t(
              "Once it's accepted by the provider, you will be able to track its status on the Transaction History page"
            )}
          />
        )}

        {(isBank || isPixCase) && (
          <DepositBankDetails
            reference={transaction?.referenceId}
            onReferenceCopy={() => setIsReferenceCopied(true)}
            details={provider?.parameters?.form ?? []}
            amountValue={formatMoney(amountValue, summaryCurrency)}
            dataKeyValue={dataKeyValue}
          />
        )}

        <FullScreenFooter contentRef={contentRef}>
          <Box
            flex
            direction={isMobile ? 'column' : 'row'}
            align='center'
            gap='x16'
            style={{ maxWidth: 480 }}
            fullWidth
            mb='x16'
          >
            <Button.Primary
              size='lg'
              fullWidth
              onClick={() => handleActionWithReferenceCheck(onViewHistory)}
            >
              {t('View History')}
            </Button.Primary>
            <Button.Secondary
              size='lg'
              fullWidth
              onClick={() => handleActionWithReferenceCheck(onNewDeposit)}
            >
              {t('New Deposit')}
            </Button.Secondary>
          </Box>
        </FullScreenFooter>
      </Box>
    </FundingTemplate>
  )
}
